import React, { FC, useEffect, useRef } from 'react';
import { Moment } from 'moment';
import {
  clampMins,
  formatDuration,
  formatTimeOfDay,
  minsOfDay,
  snap,
  SNAP_MINS,
  TOTAL_MINS,
  TViewMode,
  withTimeOfDay,
} from './timeRange.utils';
import {
  SBarSection,
  SBarWrap,
  SChip,
  SChips,
  SDurationText,
  SHandle,
  SHandleTip,
  SLabelLeft,
  SLabelRow,
  SMultiDay,
  SRange,
  SRangeLabel,
  STick,
  STickLabel,
  STickLabels,
  STicks,
} from './TimeRangeBar.style';

const QUICK_CHIPS = [
  { label: '+15 min', mins: 15 },
  { label: '+30 min', mins: 30 },
  { label: '+1 hr', mins: 60 },
  { label: '+2 hr', mins: 120 },
];

const TICK_HOURS = [0, 3, 6, 9, 12, 15, 18, 21];

type TDragMode = 'start' | 'end' | 'range' | null;

type Props = {
  startTime: Moment;
  endTime: Moment;
  viewMode: TViewMode;
  onViewModeChange: (mode: TViewMode) => void;
  onChange: (start: Moment, end: Moment) => void;
};

const pct = (mins: number): number => (mins / TOTAL_MINS) * 100;

const tickLabel = (hour: number, mode: TViewMode): string => {
  if (mode === '24') return String(hour).padStart(2, '0');
  if (hour === 0) return '12a';
  if (hour === 12) return '12p';
  return hour < 12 ? `${hour}a` : `${hour - 12}p`;
};

const TimeRangeBar: FC<Props> = ({
  startTime,
  endTime,
  viewMode,
  onViewModeChange,
  onChange,
}) => {
  const barRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    mode: TDragMode;
    startX: number;
    startMins0: number;
    endMins0: number;
  }>({ mode: null, startX: 0, startMins0: 0, endMins0: 0 });
  // A drag ends with a synthetic `click` on the bar; this flag lets us ignore
  // that click so releasing a handle doesn't re-trigger click-to-reposition.
  const suppressClickRef = useRef(false);

  const startMins = minsOfDay(startTime);
  const endMins = minsOfDay(endTime);
  const sameDay = startTime.isSame(endTime, 'day');
  // Full duration across days (used for chips/labels and the footer badge).
  const durationMins = Math.max(0, endTime.diff(startTime, 'minutes'));
  const activeChip =
    sameDay && endMins > startMins ? endMins - startMins : null;

  // Visual fill: when the entry spans into a later day, fill to end-of-day.
  const fillStart = pct(startMins);
  const fillEnd = sameDay ? pct(endMins) : 100;
  const fillWidth = Math.max(0, fillEnd - fillStart);

  // Latest range + emitter, read by the once-subscribed drag listeners so they
  // stay current without re-binding window listeners on every render.
  const latestRef = useRef({ startTime, endTime, onChange });
  latestRef.current = { startTime, endTime, onChange };

  const pointerToMins = (clientX: number): number => {
    const el = barRef.current;
    if (!el) return startMins;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    return clampMins(snap((x / rect.width) * TOTAL_MINS));
  };

  // Dragging a handle collapses the range onto a single day so the 24h bar
  // stays coherent — otherwise a next-day end sits pinned at the far edge and
  // looks undraggable. The end snaps to the start's day; the start to the
  // end's day. (For same-day ranges both dates are equal, so this is a no-op.)
  useEffect(() => {
    const onMove = (e: MouseEvent | TouchEvent) => {
      const drag = dragRef.current;
      if (!drag.mode) return;
      const el = barRef.current;
      if (!el) return;
      // Stop the page scrolling while dragging on touch devices.
      if ('touches' in e) e.preventDefault();
      const { startTime: s, endTime: en, onChange: emit } = latestRef.current;
      const sMins = minsOfDay(s);
      const eMins = minsOfDay(en);
      const rect = el.getBoundingClientRect();
      const clientX =
        'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const mins = clampMins(
        snap(((clientX - rect.left) / rect.width) * TOTAL_MINS),
      );
      if (drag.mode === 'start') {
        emit(withTimeOfDay(en, Math.min(mins, eMins - SNAP_MINS)), en);
      } else if (drag.mode === 'end') {
        emit(s, withTimeOfDay(s, Math.max(mins, sMins + SNAP_MINS)));
      } else {
        const deltaMins = snap(
          ((clientX - drag.startX) / rect.width) * TOTAL_MINS,
        );
        const span = drag.endMins0 - drag.startMins0;
        let ns = drag.startMins0 + deltaMins;
        let ne = drag.endMins0 + deltaMins;
        if (ns < 0) {
          ns = 0;
          ne = span;
        }
        if (ne > TOTAL_MINS) {
          ne = TOTAL_MINS;
          ns = TOTAL_MINS - span;
        }
        emit(
          withTimeOfDay(s, ns),
          withTimeOfDay(s, Math.min(ne, TOTAL_MINS - SNAP_MINS)),
        );
      }
    };
    const onUp = () => {
      if (dragRef.current.mode) {
        // Swallow the click that fires right after this drag release.
        suppressClickRef.current = true;
      }
      dragRef.current.mode = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  const beginDrag =
    (mode: TDragMode) => (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const clientX =
        'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
      dragRef.current = {
        mode,
        startX: clientX,
        startMins0: startMins,
        // Cap the span at a single day so a >24h (multi-day) range drag can't
        // push the clamped start below zero and produce a malformed time.
        endMins0: sameDay
          ? endMins
          : Math.min(startMins + durationMins, TOTAL_MINS),
      };
    };

  const onBarClick = (e: React.MouseEvent) => {
    // Ignore the click synthesized at the end of a handle/range drag.
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    // Ignore clicks that originate on the handles/range (those are drags).
    if ((e.target as HTMLElement).closest('[data-handle]')) return;
    const clicked = pointerToMins(e.clientX);
    const dur = durationMins > 0 ? durationMins : 60;
    const newStart = withTimeOfDay(startTime, clicked);
    onChange(newStart, newStart.clone().add(dur, 'minutes'));
  };

  const applyChip = (mins: number) => {
    onChange(startTime, startTime.clone().add(mins, 'minutes'));
  };

  const rangeText = `${formatTimeOfDay(
    startMins,
    viewMode,
  )} – ${formatTimeOfDay(endMins, viewMode)}`;
  const spanDays = endTime
    .clone()
    .startOf('day')
    .diff(startTime.clone().startOf('day'), 'days');

  return (
    <SBarSection>
      <SLabelRow>
        <SLabelLeft>
          Time range
          <SDurationText>{formatDuration(durationMins)}</SDurationText>
        </SLabelLeft>
        <div style={{ display: 'inline-flex', gap: 4 }}>
          <SChip
            type="button"
            active={viewMode === '12'}
            onClick={() => onViewModeChange('12')}
          >
            12 hr
          </SChip>
          <SChip
            type="button"
            active={viewMode === '24'}
            onClick={() => onViewModeChange('24')}
          >
            24 hr
          </SChip>
        </div>
      </SLabelRow>

      <SBarWrap ref={barRef} onClick={onBarClick} data-testid="tr-bar">
        <STicks>
          {Array.from({ length: 25 }, (_, h) => (
            <STick key={h} leftPct={(h / 24) * 100} />
          ))}
        </STicks>

        <SRange
          leftPct={fillStart}
          widthPct={fillWidth}
          data-handle
          data-testid="tr-range"
          onMouseDown={beginDrag('range')}
          onTouchStart={beginDrag('range')}
        >
          {durationMins > 35 && sameDay && (
            <SRangeLabel>{rangeText}</SRangeLabel>
          )}
        </SRange>

        <SHandle
          leftPct={fillStart}
          data-handle
          data-testid="tr-handle-start"
          onMouseDown={beginDrag('start')}
          onTouchStart={beginDrag('start')}
        >
          <SHandleTip>{formatTimeOfDay(startMins, viewMode)}</SHandleTip>
        </SHandle>
        <SHandle
          leftPct={sameDay ? pct(endMins) : 100}
          data-handle
          data-testid="tr-handle-end"
          onMouseDown={beginDrag('end')}
          onTouchStart={beginDrag('end')}
        >
          <SHandleTip>{formatTimeOfDay(endMins, viewMode)}</SHandleTip>
        </SHandle>
      </SBarWrap>

      <STickLabels>
        {TICK_HOURS.map(h => (
          <STickLabel key={h} leftPct={(h / 24) * 100}>
            {tickLabel(h, viewMode)}
          </STickLabel>
        ))}
      </STickLabels>

      <SChips>
        {QUICK_CHIPS.map(chip => (
          <SChip
            key={chip.mins}
            type="button"
            active={activeChip === chip.mins}
            onClick={() => applyChip(chip.mins)}
          >
            {chip.label}
          </SChip>
        ))}
      </SChips>

      {!sameDay && (
        <SMultiDay>
          <span>⚠</span>
          <span>
            Entry spans {spanDays} day{spanDays !== 1 ? 's' : ''} ·{' '}
            {formatDuration(durationMins)} total
          </span>
        </SMultiDay>
      )}
    </SBarSection>
  );
};

export default TimeRangeBar;
